import React, { useState, useEffect, useCallback, useRef } from 'react';
import { formatCurrencyWhole as formatCurrency } from '../../lib/format';
import {
  AlertCircle,
  Clock,
  CheckCircle,
  UserPlus,
  Search,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  DollarSign,
  Calendar,
  TrendingUp
} from 'lucide-react';
import { apiClient } from '../../lib/api';

type ReorderStatus = 'overdue' | 'due_soon' | 'recent' | 'new';

interface PhysicianRanking {
  physicianId: string;
  physicianName: string;
  clinicName: string;
  totalOrders: number;
  totalSpend: number;
  averageOrderValue: number;
  lastOrderDate: string | null;
  daysSinceLastOrder: number | null;
  averageOrderInterval: number | null;
  reorderScore: number;
  reorderStatus: ReorderStatus;
}

interface ReorderRankingTableProps {
  compact?: boolean; // For dashboard view (shows fewer columns)
  limit?: number;    // For dashboard view (limit rows)
  showFilters?: boolean;
  showPagination?: boolean;
  distributorId?: string; // For admin viewing specific distributor
  onViewAll?: () => void; // Callback when "View All" is clicked
}

const statusConfig: Record<ReorderStatus, {
  label: string;
  icon: React.ElementType;
  bgColor: string;
  textColor: string;
  borderColor: string;
}> = {
  overdue: {
    label: 'Big Opportunity',
    icon: TrendingUp,
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-700',
    borderColor: 'border-purple-200'
  },
  due_soon: {
    label: 'Follow Up',
    icon: Clock,
    bgColor: 'bg-yellow-100',
    textColor: 'text-yellow-700',
    borderColor: 'border-yellow-200'
  },
  recent: {
    label: 'Active',
    icon: CheckCircle,
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    borderColor: 'border-green-200'
  },
  new: {
    label: 'New Clinic',
    icon: UserPlus,
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-200'
  }
};

export function ReorderRankingTable({
  compact = false,
  limit,
  showFilters = true,
  showPagination = true,
  distributorId,
  onViewAll
}: ReorderRankingTableProps) {
  const [rankings, setRankings] = useState<PhysicianRanking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<ReorderStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  // What was last asked of the server; typing is debounced.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const fetchSeq = useRef(0);
  const lastSearch = useRef('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: limit || 20,
    totalCount: 0,
    totalPages: 0,
    hasMore: false
  });
  const [summary, setSummary] = useState({
    overdue: 0,
    dueSoon: 0,
    recent: 0,
    new: 0
  });

  // A new search goes back to page 1 in the same update, so the old page is
  // never requested for it.
  useEffect(() => {
    const t = setTimeout(() => {
      const next = searchQuery.trim();
      if (next === lastSearch.current) return;
      lastSearch.current = next;
      setDebouncedSearch(next);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const fetchRankings = useCallback(async () => {
    // Responses that arrive after a newer request are dropped.
    const mine = ++fetchSeq.current;
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.getPhysicianReorderRankings({
        page,
        limit: limit || 20,
        status: statusFilter,
        search: debouncedSearch,
        distributorId
      });
      if (mine !== fetchSeq.current) return;

      if (response.success && response.data) {
        setRankings(response.data.rankings);
        setPagination(response.data.pagination);
        setSummary(response.data.summary);
      } else {
        setError(response.error || 'Failed to fetch rankings');
      }
    } catch (err) {
      if (mine === fetchSeq.current) setError('Failed to fetch rankings');
    } finally {
      if (mine === fetchSeq.current) setLoading(false);
    }
  }, [page, statusFilter, debouncedSearch, limit, distributorId]);

  useEffect(() => {
    fetchRankings();
  }, [fetchRankings]);


  const formatDays = (days: number | null) => {
    if (days === null) return 'Never';
    if (days === 0) return 'Today';
    if (days === 1) return '1 day';
    return `${days} days`;
  };

  const StatusBadge = ({ status }: { status: ReorderStatus }) => {
    const config = statusConfig[status];
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const StatusFilterChip = ({ status, count, isActive }: { status: ReorderStatus | 'all'; count: number; isActive: boolean }) => {
    const getConfig = () => {
      if (status === 'all') {
        return {
          label: 'All',
          bgActive: 'bg-primary-600 text-white',
          bgInactive: 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        };
      }
      const config = statusConfig[status];
      return {
        label: config.label,
        bgActive: `${config.bgColor} ${config.textColor} ring-2 ring-offset-1 ${config.borderColor}`,
        bgInactive: `${config.bgColor} ${config.textColor} opacity-60 hover:opacity-100`
      };
    };
    const config = getConfig();

    return (
      <button
        onClick={() => {
          setStatusFilter(status);
          setPage(1);
        }}
        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
          isActive ? config.bgActive : config.bgInactive
        }`}
      >
        {config.label} ({count})
      </button>
    );
  };

  if (loading && rankings.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-primary-600" />
        <span className="ml-2 text-gray-600">Loading rankings...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8 text-red-600">
        <AlertCircle className="w-5 h-5 mr-2" />
        {error}
      </div>
    );
  }

  const totalPhysicians = summary.overdue + summary.dueSoon + summary.recent + summary.new;

  return (
    <div className="space-y-4">
      {/* Filters */}
      {showFilters && (
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          {/* Status Filter Chips */}
          <div className="flex flex-wrap gap-2">
            <StatusFilterChip status="all" count={totalPhysicians} isActive={statusFilter === 'all'} />
            <StatusFilterChip status="overdue" count={summary.overdue} isActive={statusFilter === 'overdue'} />
            <StatusFilterChip status="due_soon" count={summary.dueSoon} isActive={statusFilter === 'due_soon'} />
            <StatusFilterChip status="recent" count={summary.recent} isActive={statusFilter === 'recent'} />
            <StatusFilterChip status="new" count={summary.new} isActive={statusFilter === 'new'} />
          </div>

          {/* Search */}
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search clinic or physician..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Clinic
              </th>
              {!compact && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Physician
                </th>
              )}
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Order
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Spend
              </th>
              {!compact && (
                <>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Orders
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Avg Interval
                  </th>
                </>
              )}
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rankings.length === 0 ? (
              <tr>
                <td colSpan={compact ? 4 : 7} className="px-4 py-8 text-center text-gray-500">
                  {searchQuery || statusFilter !== 'all'
                    ? 'No clinics match your filters'
                    : 'No clinics in your network yet'}
                </td>
              </tr>
            ) : (
              rankings.map((ranking, index) => (
                <tr
                  key={ranking.physicianId}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-gray-400 text-sm mr-3 w-6">#{(page - 1) * pagination.limit + index + 1}</span>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{ranking.clinicName || ranking.physicianName}</div>
                        {compact && ranking.clinicName && (
                          <div className="text-xs text-gray-500">{ranking.physicianName}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  {!compact && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {ranking.physicianName}
                    </td>
                  )}
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Calendar className="w-3.5 h-3.5 text-gray-400" />
                      <span className={`text-sm ${ranking.daysSinceLastOrder === null ? 'text-gray-400' : 'text-gray-900'}`}>
                        {formatDays(ranking.daysSinceLastOrder)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1">
                      <DollarSign className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(ranking.totalSpend)}
                      </span>
                    </div>
                  </td>
                  {!compact && (
                    <>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-500">
                        {ranking.totalOrders}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-500">
                        {ranking.averageOrderInterval ? `${ranking.averageOrderInterval} days` : '-'}
                      </td>
                    </>
                  )}
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    <StatusBadge status={ranking.reorderStatus} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination / View All */}
      {showPagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            Showing {((page - 1) * pagination.limit) + 1} to {Math.min(page * pagination.limit, pagination.totalCount)} of {pagination.totalCount}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-gray-700">
              Page {page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
              disabled={page === pagination.totalPages}
              className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* View All Link for Compact Mode */}
      {compact && onViewAll && rankings.length > 0 && (
        <div className="text-center pt-2">
          <button
            onClick={onViewAll}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            View All Rankings
          </button>
        </div>
      )}
    </div>
  );
}
